import React, { useState } from 'react';
import Modal from './ui/Modal';
import { fsSet } from '../lib/firebase';
import { fmtDayDate } from '../lib/datetime';
import { renderCaregiverDeclineMessage, smsHref } from '../lib/sms';
import { DEFAULT_COVERAGE_MESSAGES, SESSION_STATUS } from '../config/appConfig';

// ─── SESSION CANCEL SHEET ──────────────────────────────────────────────────────
// A caregiver declining a booked session, from their tokenized "My Sessions"
// view. Modeled on CoverageRequestSheet's confirm → action shape, but
// simpler (always both parents, no candidate picker) and writes only to the
// sessions doc — a token-based caregiver has no Google Calendar access
// (never authenticated with Google), so the calendar event itself gets
// reconciled later by a parent's browser (see LogisticsModule's
// reconcile-on-read). No fsLogChange call either — there's no authenticated
// actor to attribute the change to.

export default function SessionCancelSheet({ familyData, session, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const template = familyData?.config?.coverageMessages?.caregiverDecline || DEFAULT_COVERAGE_MESSAGES.caregiverDecline;
  const message  = renderCaregiverDeclineMessage(template, { session, carePartners: familyData?.carePartners });
  const parents  = (familyData?.carePartners || []).filter(cp => cp.phone?.trim());

  async function handleDecline() {
    if (!familyData?.id || !session?.id) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      // Permitted by the field-restricted public rule on sessions — see
      // firestore.rules — only these fields, only scheduled -> declined.
      await fsSet(`families/${familyData.id}/sessions/${session.id}`, {
        status: SESSION_STATUS.DECLINED,
        declinedAt: new Date().toISOString(),
        declinedReason: null,
      });
      setSent(true);
    } catch (err) {
      console.error('SessionCancelSheet decline failed:', err);
      setErrorMsg("Couldn't cancel this session — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Cancel this session" onClose={onClose}>
      {!sent ? (
        <div className="coverage-sheet">
          <p className="coverage-sheet__recap">
            <strong>{fmtDayDate(session.date)}</strong>
            <span className="coverage-sheet__recap-time">{session.startTime}–{session.endTime}</span>
          </p>
          <p className="coverage-sheet__hint">
            This marks the session as declined and lets the family know you can't make it — nothing is deleted from their calendar, so it stays visible until they find someone else.
          </p>
          {errorMsg && <p className="coverage-sheet__error">{errorMsg}</p>}
          <button className="btn btn--primary" onClick={handleDecline} disabled={busy}>
            {busy ? 'Cancelling…' : 'Yes, cancel this session'}
          </button>
        </div>
      ) : (
        <div className="coverage-sheet">
          <p className="coverage-sheet__hint">Marked as declined. Let the family know directly:</p>
          {parents.length === 0 ? (
            <p className="coverage-sheet__empty">No parent phone numbers on file.</p>
          ) : (
            <div className="coverage-sheet__candidates">
              {parents.map(cp => (
                <a key={cp.id} href={smsHref(cp.phone, message)} className="coverage-sheet__candidate"
                  onClick={() => onDone?.()}>
                  <span className="coverage-sheet__candidate-name">Text {cp.name?.split(' ')[0] || cp.name}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
